import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { comprehensiveHRService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceReviews = ({ hrProfiles, loading, gymId, managerId }) => {
  const [reviews, setReviews] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  useEffect(() => {
    loadReviews();
  }, [gymId]);

  const loadReviews = async () => {
    if (!gymId) return;
    setReviewsLoading(true);
    const { data } = await comprehensiveHRService?.getGymReviews(gymId);
    if (data) setReviews(data);
    setReviewsLoading(false);
  };

  const handleCreateReview = async (reviewData) => {
    const hrProfile = hrProfiles?.find((p) => p?.staff_id === reviewData?.staffId);
    if (!hrProfile) return;

    const overallRating =
      (parseFloat(reviewData?.punctualityRating) +
        parseFloat(reviewData?.performanceRating) +
        parseFloat(reviewData?.clientFeedbackRating)) /
      3;

    const { error } = await comprehensiveHRService?.createPerformanceReview({
      gymId,
      staffId: reviewData?.staffId,
      hrProfileId: hrProfile?.id,
      reviewerId: managerId,
      reviewCycle: reviewData?.reviewCycle,
      reviewDate: reviewData?.reviewDate,
      reviewPeriodStart: reviewData?.reviewPeriodStart,
      reviewPeriodEnd: reviewData?.reviewPeriodEnd,
      punctualityRating: parseInt(reviewData?.punctualityRating),
      performanceRating: parseInt(reviewData?.performanceRating),
      clientFeedbackRating: parseInt(reviewData?.clientFeedbackRating),
      strengths: reviewData?.strengths,
      areasForImprovement: reviewData?.areasForImprovement,
      goals: reviewData?.goals,
      managerNotes: reviewData?.managerNotes,
      overallRating: overallRating?.toFixed(2)
    });

    if (!error) {
      setShowCreateModal(false);
      setSelectedStaff(null);
      loadReviews();
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-success';
    if (rating >= 3.5) return 'text-primary';
    if (rating >= 2.5) return 'text-warning';
    return 'text-error';
  };

  const avgOverallRating =
    reviews?.length > 0
      ? reviews?.reduce((sum, r) => sum + (parseFloat(r?.overall_rating) || 0), 0) / reviews?.length
      : 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Award" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{reviews?.length}</div>
              <div className="text-xs text-muted-foreground">Total Reviews</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-success/10">
              <Icon name="Star" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{avgOverallRating?.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">Avg Rating</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="Calendar" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {reviews?.filter((r) => r?.review_cycle === 'quarterly')?.length}
              </div>
              <div className="text-xs text-muted-foreground">Quarterly Reviews</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="TrendingUp" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">
                {reviews?.filter((r) => parseFloat(r?.overall_rating) >= 4.0)?.length}
              </div>
              <div className="text-xs text-muted-foreground">High Performers</div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="card">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Performance Reviews</h2>
          <Button onClick={() => setShowCreateModal(true)}>
            <Icon name="Plus" size={18} />
            <span>Create Review</span>
          </Button>
        </div>
      </div>

      {/* Reviews List */}
      {reviewsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : reviews?.length === 0 ? (
        <div className="card text-center py-12">
          <Icon name="Award" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Reviews Yet</h3>
          <p className="text-muted-foreground">Create your first performance review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews?.map((review) => (
            <div key={review?.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{review?.staff?.full_name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {review?.review_cycle} Review - {review?.staff?.role}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(review?.review_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold ${getRatingColor(parseFloat(review?.overall_rating))}`}>
                    {parseFloat(review?.overall_rating)?.toFixed(1)}
                  </div>
                  <div className="text-xs text-muted-foreground">Overall Rating</div>
                </div>
              </div>

              {/* Rating Breakdown */}
              <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{review?.punctuality_rating}</div>
                  <div className="text-xs text-muted-foreground">Punctuality</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{review?.performance_rating}</div>
                  <div className="text-xs text-muted-foreground">Performance</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{review?.client_feedback_rating}</div>
                  <div className="text-xs text-muted-foreground">Client Feedback</div>
                </div>
              </div>

              {/* Review Details */}
              <div className="space-y-3">
                {review?.strengths && (
                  <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                    <div className="text-sm font-medium text-success mb-1 flex items-center gap-2">
                      <Icon name="ThumbsUp" size={16} />
                      Strengths
                    </div>
                    <p className="text-sm text-foreground">{review?.strengths}</p>
                  </div>
                )}

                {review?.areas_for_improvement && (
                  <div className="p-3 rounded-lg bg-warning/5 border border-warning/20">
                    <div className="text-sm font-medium text-warning mb-1 flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} />
                      Areas for Improvement
                    </div>
                    <p className="text-sm text-foreground">{review?.areas_for_improvement}</p>
                  </div>
                )}

                {review?.goals && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="text-sm font-medium text-primary mb-1 flex items-center gap-2">
                      <Icon name="Target" size={16} />
                      Goals
                    </div>
                    <p className="text-sm text-foreground">{review?.goals}</p>
                  </div>
                )}

                {review?.manager_notes && (
                  <div className="p-3 rounded-lg bg-muted/30">
                    <div className="text-sm font-medium text-muted-foreground mb-1">Manager Notes</div>
                    <p className="text-sm text-foreground">{review?.manager_notes}</p>
                  </div>
                )}
              </div>

              {/* Metrics */}
              {(review?.sessions_completed || review?.attendance_rate) && (
                <div className="mt-4 pt-4 border-t border-border flex items-center gap-6 text-sm">
                  {review?.sessions_completed && (
                    <div>
                      <span className="text-muted-foreground">Sessions: </span>
                      <span className="font-medium text-foreground">{review?.sessions_completed}</span>
                    </div>
                  )}
                  {review?.attendance_rate && (
                    <div>
                      <span className="text-muted-foreground">Attendance: </span>
                      <span className="font-medium text-foreground">{review?.attendance_rate}%</span>
                    </div>
                  )}
                  {review?.revenue_contribution && (
                    <div>
                      <span className="text-muted-foreground">Revenue: </span>
                      <span className="font-medium text-success">
                        ${parseFloat(review?.revenue_contribution)?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
                Reviewed by {review?.reviewer?.full_name} •{' '}
                {review?.review_period_start && review?.review_period_end
                  ? `Period: ${format(new Date(review?.review_period_start), 'MMM d')} - ${format(
                      new Date(review?.review_period_end),
                      'MMM d, yyyy'
                    )}`
                  : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Review Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Create Performance Review</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e?.preventDefault();
                const formData = new FormData(e?.target);
                handleCreateReview({
                  staffId: formData?.get('staffId'),
                  reviewCycle: formData?.get('reviewCycle'),
                  reviewDate: formData?.get('reviewDate'),
                  reviewPeriodStart: formData?.get('reviewPeriodStart'),
                  reviewPeriodEnd: formData?.get('reviewPeriodEnd'),
                  punctualityRating: formData?.get('punctualityRating'),
                  performanceRating: formData?.get('performanceRating'),
                  clientFeedbackRating: formData?.get('clientFeedbackRating'),
                  strengths: formData?.get('strengths'),
                  areasForImprovement: formData?.get('areasForImprovement'),
                  goals: formData?.get('goals'),
                  managerNotes: formData?.get('managerNotes')
                });
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Staff Member *</label>
                  <select
                    name="staffId"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select staff member</option>
                    {hrProfiles?.map((profile) => (
                      <option key={profile?.staff_id} value={profile?.staff_id}>
                        {profile?.staff?.full_name} - {profile?.position}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Review Cycle *</label>
                  <select
                    name="reviewCycle"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                    <option value="probation">Probation</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Review Date *</label>
                  <input
                    type="date"
                    name="reviewDate"
                    required
                    defaultValue={format(new Date(), 'yyyy-MM-dd')}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Period Start</label>
                  <input
                    type="date"
                    name="reviewPeriodStart"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Period End</label>
                  <input
                    type="date"
                    name="reviewPeriodEnd"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Punctuality (1-5) *
                  </label>
                  <input
                    type="number"
                    name="punctualityRating"
                    required
                    min="1"
                    max="5"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Performance (1-5) *
                  </label>
                  <input
                    type="number"
                    name="performanceRating"
                    required
                    min="1"
                    max="5"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Client Feedback (1-5) *
                  </label>
                  <input
                    type="number"
                    name="clientFeedbackRating"
                    required
                    min="1"
                    max="5"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Strengths</label>
                <textarea
                  name="strengths"
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="What are this employee's key strengths?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Areas for Improvement
                </label>
                <textarea
                  name="areasForImprovement"
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="What areas need improvement?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Goals</label>
                <textarea
                  name="goals"
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Set goals for the next review period"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Manager Notes</label>
                <textarea
                  name="managerNotes"
                  rows={2}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Additional notes (internal only)"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  Create Review
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceReviews;